import type { Text } from 'pixi.js';
import type { INameTag, NameTagStyle } from './IRenderer';
import { PixiOverlayHost } from './PixiOverlayHost';
import { prefersReducedMotion } from '../systems/accessibility/motionPrefs';
import { computePortalPromptPlacement } from './portalPromptPlacement';
import { getHudOcclusionRects } from './hudOcclusionZones';
import { NAMEPLATE, computeReadableWorldScale } from '../../shared/config/nameplate.config';

/**
 * PixiNameTag — INameTag impl backed by a PIXI.Text on the overlay's
 * worldContainer. Same lazy-build pattern as PixiBarLabel: queued
 * setText / setPosition / setVisible / setAlpha buffered until
 * ensureReady() resolves so consumers can construct + immediately
 * mutate without race risk.
 */
export class PixiNameTag implements INameTag {
  private text: Text | null = null;
  private destroyed = false;

  private baseX: number;
  private baseY: number;
  private pendingX: number;
  private pendingY: number;
  private pendingText: string;
  private pendingVisible = true;
  private pendingAlpha = 1;
  private pendingScale = 1;
  private depth: number;
  private fontFamily: string;
  private fontSize: number;
  private color: number;
  private anchorX: number;
  private anchorY: number;
  private fontWeight: 'normal' | 'bold';
  private strokeWidth: number;
  private letterSpacing: number;
  private textResolution: number;
  private roundPixels: boolean;
  private dropShadow: NameTagStyle['dropShadow'];
  private alphaPulse: NameTagStyle['alphaPulse'];
  private screenSpace: boolean;
  private clampToViewport: NameTagStyle['clampToViewport'];
  private wordWrap: boolean;
  private wordWrapWidth?: number;
  /**
   * Bounded compact-viewport readability multiplier (U2). 1 on desktop-fit
   * viewports; up to NAMEPLATE.mobFit.maxReadableScale on fitted/compact
   * surfaces. Recomputed from live screen/camera info on setPosition — world
   * plates are repositioned every frame, so no tick listener is needed.
   */
  private readableMultiplier = 1;
  private pulseFrame: number | null = null;
  private pulseMultiplier = 1;
  private unbindTick: (() => void) | null = null;

  constructor(x: number, y: number, initialText: string, style: NameTagStyle = {}) {
    this.baseX = x;
    this.baseY = y;
    this.pendingX = x;
    this.pendingY = y;
    this.pendingText = initialText;
    this.depth = style.depth ?? 15;
    this.fontFamily = style.fontFamily ?? 'Arial, "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
    this.fontSize = style.fontSize ?? 12;
    this.color = style.color ?? 0xffffff;
    this.anchorX = style.anchor?.x ?? 0.5;
    this.anchorY = style.anchor?.y ?? 0.5;
    this.fontWeight = style.fontWeight ?? 'normal';
    this.strokeWidth = style.strokeWidth ?? 2;
    this.letterSpacing = style.letterSpacing ?? 0;
    this.textResolution = style.textResolution ?? 1;
    this.roundPixels = style.roundPixels ?? false;
    this.dropShadow = style.dropShadow;
    this.alphaPulse = style.alphaPulse;
    this.screenSpace = style.screenSpace ?? false;
    this.clampToViewport = style.clampToViewport;
    this.wordWrap = style.wordWrap ?? false;
    this.wordWrapWidth = style.wordWrapWidth;

    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      const textStyle = {
        fontFamily: this.fontFamily,
        fontSize: this.fontSize,
        fill: this.color,
        stroke: this.strokeWidth > 0 ? { color: 0x000000, width: this.strokeWidth } : undefined,
        align: 'center' as const,
        fontWeight: this.fontWeight,
        letterSpacing: this.letterSpacing,
        dropShadow: this.dropShadow,
        wordWrap: this.wordWrap,
        wordWrapWidth: this.wordWrapWidth,
        // U2 — long player-style enemy names have no spaces; without
        // breakWords the wrap width would be ignored and the plate would
        // overrun its sprite again.
        breakWords: this.wordWrap,
      };
      // Screen-space text attaches to UI container (camera-independent);
      // world-space (default) attaches to worldContainer (camera-bound).
      const t = this.screenSpace
        ? PixiOverlayHost.createUiText('', textStyle)
        : PixiOverlayHost.createWorldText('', textStyle);
      t.anchor.set(this.anchorX, this.anchorY);
      t.resolution = this.textResolution;
      t.roundPixels = this.roundPixels;
      t.zIndex = this.depth;
      t.x = this.pendingX;
      t.y = this.pendingY;
      t.text = this.pendingText;
      t.visible = this.pendingVisible;
      t.alpha = this.pendingAlpha * this.pulseMultiplier;
      t.scale.set(this.pendingScale, this.pendingScale);
      this.text = t;
      this.applyWorldReadableScale();
      this.startAlphaPulse();

      if (this.clampToViewport) {
        this.updateClampedPosition();
        this.unbindTick = PixiOverlayHost.addTickListener(() => {
          this.updateClampedPosition();
        });
      }
    });
  }

  private updateClampedPosition(): void {
    if (!this.text || this.destroyed || !this.clampToViewport) return;

    const screenSize = PixiOverlayHost.getScreenSize();
    const cameraView = PixiOverlayHost.getCameraView();
    if (!screenSize || !cameraView) {
      this.text.x = this.baseX;
      this.text.y = this.baseY;
      return;
    }

    const fitScale = Math.min(
      screenSize.width / cameraView.width,
      screenSize.height / cameraView.height,
    );
    const parentScaleX = this.text.parent?.scale?.x ?? fitScale;
    const zoom = fitScale > 0 ? parentScaleX / fitScale : 1;

    const margin =
      typeof this.clampToViewport === 'object' &&
      typeof this.clampToViewport.margin === 'number'
        ? this.clampToViewport.margin
        : 16;
    const horizontalOnly =
      typeof this.clampToViewport === 'object' &&
      typeof this.clampToViewport.horizontalOnly === 'boolean'
        ? this.clampToViewport.horizontalOnly
        : true;

    // Apply responsive word wrapping and maintain readable scale on mobile viewports
    const scale = fitScale * (zoom > 0 ? zoom : 1);
    const renderedW = cameraView.width * scale;
    const viewportLeft = Math.max(0, (screenSize.width - renderedW) / 2);
    const viewportRight = Math.min(screenSize.width, (screenSize.width + renderedW) / 2);
    const availableScreenWidth = Math.max(120, (viewportRight - margin) - (viewportLeft + margin));

    // Ensure readable minimum scale on narrow mobile viewports so text remains legible
    const readableScale = scale < 1 ? 1 / scale : 1;
    const labelScale = readableScale * this.pendingScale;
    this.text.scale.set(labelScale, labelScale);

    // Total scale from local text units to canvas screen pixels
    const totalRenderScale = scale * labelScale;

    // Word wrapping must use the same coordinate units (local text glyph units).
    // availableScreenWidth is in screen pixels; totalRenderScale converts local text units to screen pixels.
    // If explicit wordWrapWidth was provided in style (world units), scale it accordingly.
    const maxWorldWrapWidth = this.wordWrapWidth ? this.wordWrapWidth / labelScale : Infinity;
    const targetWrapWidth = Math.min(availableScreenWidth / totalRenderScale, maxWorldWrapWidth);

    if (!this.text.style.wordWrap || Math.abs((this.text.style.wordWrapWidth || 0) - targetWrapWidth) > 1) {
      this.text.style.wordWrap = true;
      this.text.style.wordWrapWidth = targetWrapWidth;
    }

    const placement = computePortalPromptPlacement({
      worldPosition: { x: this.baseX, y: this.baseY },
      textDimensions: { width: this.text.width, height: this.text.height },
      textScale: 1, // Contract: text.width is in world units (already includes labelScale)
      anchor: { x: this.anchorX, y: this.anchorY },
      canvasSize: screenSize,
      cameraState: {
        width: cameraView.width,
        height: cameraView.height,
        scrollX: cameraView.centerX - cameraView.width / 2,
        scrollY: cameraView.centerY - cameraView.height / 2,
        zoom,
      },
      margin,
      horizontalOnly,
      // W1a — HUD occlusion avoidance. The rects come from a layout-time store
      // (TargetInfoPanel publishes on ResizeObserver + measured layout changes):
      // no getBoundingClientRect() in this per-tick path. Coordinate spaces match
      // by construction — the Pixi canvas is mounted full-viewport at (0, 0)
      // (resizeTo: window, logical app.screen px), and the store's rects are
      // viewport logical CSS px from position:fixed HUD elements.
      avoidRects: getHudOcclusionRects(),
    });

    this.text.x = placement.worldX;
    this.text.y = placement.worldY;
  }

  private startAlphaPulse(): void {
    const pulse = this.alphaPulse;
    if (!pulse || pulse.periodMs <= 0 || pulse.amplitude <= 0 || prefersReducedMotion()) {
      return;
    }
    const startedAt = performance.now();
    const tick = (): void => {
      if (this.destroyed) return;
      const phase = ((performance.now() - startedAt) / pulse.periodMs) * Math.PI * 2;
      this.pulseMultiplier = 1 + pulse.amplitude * Math.sin(phase);
      if (this.text) this.text.alpha = this.pendingAlpha * this.pulseMultiplier;
      this.pulseFrame = requestAnimationFrame(tick);
    };
    this.pulseFrame = requestAnimationFrame(tick);
  }

  /**
   * U2 (2026-09-28) — bounded readability counter-scale for world-space
   * plates. Only world-space tags that opted into word wrapping (the mob
   * nameplates) take this path: `clampToViewport` tags keep their existing
   * per-tick clamp/readable treatment, and every other screen-space or
   * plain label is untouched (zero per-frame overhead — no tick listener).
   */
  private applyWorldReadableScale(): void {
    if (!this.text || this.destroyed) return;
    if (this.screenSpace || this.clampToViewport || !this.wordWrap) return;
    const screenSize = PixiOverlayHost.getScreenSize();
    const cameraView = PixiOverlayHost.getCameraView();
    if (!screenSize || !cameraView || cameraView.width <= 0 || cameraView.height <= 0) {
      this.setAppliedReadableScale(1);
      return;
    }
    const fitScale = Math.min(
      screenSize.width / cameraView.width,
      screenSize.height / cameraView.height,
    );
    this.setAppliedReadableScale(computeReadableWorldScale(fitScale, this.fontSize, NAMEPLATE.mobFit));
  }

  /** Apply `pendingScale × readableMultiplier` when the multiplier changes. */
  private setAppliedReadableScale(multiplier: number): void {
    const safe = Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
    if (Math.abs(safe - this.readableMultiplier) < 0.001) return;
    this.readableMultiplier = safe;
    if (this.text) {
      const total = this.pendingScale * safe;
      this.text.scale.set(total, total);
    }
  }

  setVisible(visible: boolean): void {
    this.pendingVisible = visible;
    if (this.text) this.text.visible = visible;
  }

  setPosition(x: number, y: number): void {
    this.baseX = x;
    this.baseY = y;
    this.pendingX = x;
    this.pendingY = y;
    if (this.text) {
      if (this.clampToViewport) {
        this.updateClampedPosition();
      } else {
        this.text.x = x;
        this.text.y = y;
        this.applyWorldReadableScale();
      }
    }
  }

  setText(text: string): void {
    this.pendingText = text;
    if (this.text) {
      this.text.text = text;
      if (this.clampToViewport) {
        this.updateClampedPosition();
      }
    }
  }

  setAlpha(alpha: number): void {
    this.pendingAlpha = alpha;
    if (this.text) this.text.alpha = alpha * this.pulseMultiplier;
  }

  setScale(scale: number): void {
    this.pendingScale = scale;
    if (this.text) {
      const total = scale * this.readableMultiplier;
      this.text.scale.set(total, total);
    }
  }

  setColor(color: number): void {
    this.color = color;
    if (this.text) this.text.style.fill = color;
  }

  destroy(): void {
    this.destroyed = true;
    if (this.unbindTick) {
      this.unbindTick();
      this.unbindTick = null;
    }
    if (this.pulseFrame !== null) {
      cancelAnimationFrame(this.pulseFrame);
      this.pulseFrame = null;
    }
    if (this.text) {
      this.text.destroy();
      this.text = null;
    }
  }
}
